import React, { useEffect, useState } from 'react';
import Header from '../Header';
import Header2 from '../Header2';
import Footer from '../Footer';
import {
  fetchDriverDocumentRequirements,
  uploadDriverDocument,
  submitDriverApplication,
} from '../../api/starviewApi';
import { DRIVER_COUNTRY_OPTIONS } from '../../utils/driverCountryOptions';
import './DriverApply.css';

const DESKTOP_BREAKPOINT = 900;

const VEHICLE_TYPES = ['Car', 'Van', 'Motorcycle', 'Scooter', 'Bicycle'];

const EMPTY_FORM = {
  username: '',
  email: '',
  phoneNumber: '',
  vehicleType: 'Car',
  licensePlate: '',
  vehicleMake: '',
  vehicleModel: '',
  vehicleYear: '',
  licenseExpiresAt: '',
  insuranceExpiresAt: '',
  registrationExpiresAt: '',
};

export default function DriverApply() {
  const [isDesktop, setIsDesktop] = useState(false);
  const [country, setCountry] = useState('US');
  const [requirements, setRequirements] = useState([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [loadingRequirements, setLoadingRequirements] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  // key -> { filename, uploading, error, fileName }
  const [documents, setDocuments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingRequirements(true);
    fetchDriverDocumentRequirements(country)
      .then((data) => {
        if (cancelled) return;
        setRequirements(data.requirements || []);
        setDisclaimer(data.disclaimer || '');
        // Clear any documents uploaded against the previous country's
        // checklist - the keys (and which ones are required) can differ.
        setDocuments({});
      })
      .catch(() => { if (!cancelled) setRequirements([]); })
      .finally(() => { if (!cancelled) setLoadingRequirements(false); });
    return () => { cancelled = true; };
  }, [country]);

  const updateField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleFileChange = (docKey) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocuments((d) => ({ ...d, [docKey]: { fileName: file.name, uploading: true, error: null } }));
    try {
      const result = await uploadDriverDocument(file);
      setDocuments((d) => ({ ...d, [docKey]: { fileName: file.name, uploading: false, filename: result.filename } }));
    } catch (err) {
      setDocuments((d) => ({ ...d, [docKey]: { fileName: file.name, uploading: false, error: err.message } }));
    }
  };

  const missingRequired = requirements.filter((r) => r.required && !documents[r.key]?.filename);
  const stillUploading = Object.values(documents).some((d) => d.uploading);
  const canSubmit = form.username.trim() && form.email.trim() && missingRequired.length === 0 && !stillUploading && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const documentMap = {};
      Object.entries(documents).forEach(([key, d]) => {
        if (d.filename) documentMap[key] = d.filename;
      });
      await submitDriverApplication({
        country,
        ...form,
        vehicleYear: form.vehicleYear ? Number(form.vehicleYear) : undefined,
        documents: documentMap,
        licenseExpiresAt: form.licenseExpiresAt || undefined,
        insuranceExpiresAt: form.insuranceExpiresAt || undefined,
        registrationExpiresAt: form.registrationExpiresAt || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong submitting your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="drive-root">
        <Header />
        {isDesktop && <Header2 />}
        <section className="drive-success">
          <div className="drive-success-badge">✓</div>
          <h1>Application Submitted</h1>
          <p>
            Thanks for applying to drive with StarView, {form.username}. A real reviewer checks every application by
            hand - once yours is approved, we'll email {form.email} with a link to set your password and go online.
          </p>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="drive-root">
      <Header />
      {isDesktop && <Header2 />}

      <section className="drive-hero">
        <span className="drive-eyebrow">OrderUp Delivery</span>
        <h1 className="drive-title">Drive With StarView</h1>
        <p className="drive-subtitle">
          Deliver real orders from real local restaurants straight to a moviegoer's seat. Set your own hours, keep
          100% of your tips, and get paid weekly. Apply below - it takes about five minutes.
        </p>
      </section>

      <form className="drive-form-section" onSubmit={handleSubmit}>
        <div className="drive-field-group">
          <label className="drive-label">Country</label>
          <select className="drive-input" value={country} onChange={(e) => setCountry(e.target.value)}>
            {DRIVER_COUNTRY_OPTIONS.map((c) => (
              <option key={c.iso} value={c.iso}>{c.label}</option>
            ))}
          </select>
        </div>

        <h2 className="drive-section-heading">Your Info</h2>
        <div className="drive-field-grid">
          <div className="drive-field-group">
            <label className="drive-label">Full Name</label>
            <input className="drive-input" required value={form.username} onChange={updateField('username')} placeholder="Jane Doe" />
          </div>
          <div className="drive-field-group">
            <label className="drive-label">Email</label>
            <input className="drive-input" type="email" required value={form.email} onChange={updateField('email')} placeholder="jane@example.com" />
          </div>
          <div className="drive-field-group">
            <label className="drive-label">Phone Number</label>
            <input className="drive-input" type="tel" value={form.phoneNumber} onChange={updateField('phoneNumber')} placeholder="(555) 555-0100" />
          </div>
        </div>

        <h2 className="drive-section-heading">Your Vehicle</h2>
        <div className="drive-field-grid">
          <div className="drive-field-group">
            <label className="drive-label">Vehicle Type</label>
            <select className="drive-input" value={form.vehicleType} onChange={updateField('vehicleType')}>
              {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="drive-field-group">
            <label className="drive-label">License Plate</label>
            <input className="drive-input" value={form.licensePlate} onChange={updateField('licensePlate')} placeholder="ABC-1234" />
          </div>
          <div className="drive-field-group">
            <label className="drive-label">Make</label>
            <input className="drive-input" value={form.vehicleMake} onChange={updateField('vehicleMake')} placeholder="Toyota" />
          </div>
          <div className="drive-field-group">
            <label className="drive-label">Model</label>
            <input className="drive-input" value={form.vehicleModel} onChange={updateField('vehicleModel')} placeholder="Corolla" />
          </div>
          <div className="drive-field-group">
            <label className="drive-label">Year</label>
            <input className="drive-input" type="number" min="1980" max={new Date().getFullYear() + 1} value={form.vehicleYear} onChange={updateField('vehicleYear')} placeholder="2022" />
          </div>
        </div>

        <h2 className="drive-section-heading">Documents</h2>
        {disclaimer && <p className="drive-disclaimer">{disclaimer}</p>}
        {loadingRequirements ? (
          <p className="drive-status">Loading document checklist for this country…</p>
        ) : (
          <div className="drive-documents-grid">
            {requirements.map((req) => {
              const doc = documents[req.key];
              return (
                <div key={req.key} className="drive-doc-card">
                  <label className="drive-doc-label">
                    {req.label}{req.required && <span className="drive-required">*</span>}
                  </label>
                  {req.note && <p className="drive-doc-note">{req.note}</p>}
                  <input
                    className="drive-doc-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
                    onChange={handleFileChange(req.key)}
                  />
                  {doc?.uploading && <span className="drive-doc-status uploading">Uploading…</span>}
                  {doc?.filename && <span className="drive-doc-status success">✓ {doc.fileName}</span>}
                  {doc?.error && <span className="drive-doc-status error">{doc.error}</span>}
                </div>
              );
            })}
          </div>
        )}

        <h2 className="drive-section-heading">Document Expiry Dates</h2>
        <div className="drive-field-grid">
          <div className="drive-field-group">
            <label className="drive-label">License Expires</label>
            <input className="drive-input" type="date" value={form.licenseExpiresAt} onChange={updateField('licenseExpiresAt')} />
          </div>
          <div className="drive-field-group">
            <label className="drive-label">Insurance Expires</label>
            <input className="drive-input" type="date" value={form.insuranceExpiresAt} onChange={updateField('insuranceExpiresAt')} />
          </div>
          <div className="drive-field-group">
            <label className="drive-label">Registration Expires</label>
            <input className="drive-input" type="date" value={form.registrationExpiresAt} onChange={updateField('registrationExpiresAt')} />
          </div>
        </div>

        {submitError && <p className="drive-submit-error">{submitError}</p>}
        <button type="submit" className="drive-submit-btn" disabled={!canSubmit}>
          {submitting ? 'Submitting…' : 'Submit Application'}
        </button>
        {missingRequired.length > 0 && (
          <p className="drive-missing-note">
            Still needed: {missingRequired.map((r) => r.label).join(', ')}
          </p>
        )}
      </form>

      <Footer />
    </div>
  );
}
